import type { AbstractPowerSyncDatabase } from "@powersync/common";
import { USER_DATA_TABLES } from "@comandero/powersync";

/**
 * Upper bound (ms) for the whole sweep. See the timeout rationale in
 * {@link sweepVisitorData}. Exported so tests can assert/shrink it.
 */
export const SWEEP_VISITOR_DATA_TIMEOUT_MS = 5000;

/**
 * Replaces VISITOR_ID with the newly authenticated user's ID
 * across all relevant tables and the offline CRUD queue.
 *
 * This ensures that:
 * 1. The local UI instantly reflects the new owner without flashing.
 * 2. The PowerSync upload queue sends the correct `user_id` to the backend.
 *
 * BOUNDED ON PURPOSE (same guard as the auth flow's `checkUnsyncedActor`): the
 * caller (`finalizeSignIn`) awaits this BEFORE registering the account in the
 * multi-account registry, so a sweep that never settles strands a login that
 * ALREADY succeeded on the server — the sign-in returns 200 and sets the device
 * cookie, but the registry stays empty, so no `X-Active-Account` selector is
 * ever sent, every `get-session` answers 401 and the app sits on /login forever
 * (only a reload recovers). And it CAN hang: the auth-flow machine captures the
 * PowerSync instance at mount, so after a sign-out (which destroys that
 * account's DB) or a guest-DB rebuild the handle is dead and `db.execute` may
 * never settle — a `try/catch` only ever catches REJECTIONS, never a
 * never-settling promise.
 *
 * Degrading to "sweep skipped" is SAFE: the visitor rows stay stamped with
 * VISITOR_ID (they are not lost, and a later sign-in on a live DB sweeps them),
 * whereas a stuck await breaks authentication itself.
 */
export async function sweepVisitorData(
  db: AbstractPowerSyncDatabase | null,
  newUserId: string,
  visitorId: string,
  timeoutMs: number = SWEEP_VISITOR_DATA_TIMEOUT_MS,
) {
  if (!db || !newUserId) {
    return;
  }

  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<"timeout">((resolve) => {
    timer = setTimeout(() => {
      resolve("timeout");
    }, timeoutMs);
  });

  try {
    const outcome = await Promise.race([runSweep(db, newUserId, visitorId), deadline]);
    if (outcome === "timeout") {
      console.warn(
        "[Sweep] Visitor-data sweep timed out (stale local DB handle?); continuing the sign-in.",
      );
    }
  } finally {
    clearTimeout(timer);
  }
}

async function runSweep(
  db: AbstractPowerSyncDatabase,
  newUserId: string,
  visitorId: string,
): Promise<"done"> {
  try {
    // 1. Update the actual table rows so UI queries pick them up immediately
    await Promise.all(
      USER_DATA_TABLES.map(async (table) =>
        db.execute(`UPDATE ${table} SET user_id = ? WHERE user_id = ?`, [newUserId, visitorId]),
      ),
    );

    // 2. Rewrite the queued operations so the network payload has the correct
    // user_id. PowerSync's `ps_crud.data` is a JSON envelope
    // `{"op":…,"type":…,"id":…,"data":{<column>:<value>,…}}` — the row's ACTUAL
    // column values live NESTED under `$.data.*` (see `CrudEntry.fromRow`). The
    // previous top-level `$.user_id` matched NOTHING, so the queued op stayed
    // stamped with VISITOR_ID (CC-27). (comandero's guest data is user_id-keyed
    // at the sweep level; organizations are created server-side with fresh UUIDs
    // — NOT org_id == user_id like stockpilot's WMS — so there is no client-side
    // organization_id to rewrite here.)
    await db.execute(
      `
      UPDATE ps_crud
      SET data = json_set(data, '$.data.user_id', ?)
      WHERE json_extract(data, '$.data.user_id') = ?
    `,
      [newUserId, visitorId],
    );
  } catch (error) {
    console.error("[Sweep] Failed to sweep visitor data:", error);
  }
  return "done";
}
